import type { Aabb, Billboard, Plot, WorldManifest } from "@townsy/contracts";
import { SOLID_MIN_HEIGHT } from "./catalog";
import { CIVIC_OFFICE } from "./templates";

/**
 * Street decor (ADR-0006 "solid world"): the things standing on walkable ground around the plots
 * and billboards, with their collision boxes. Positions are derived from what the renderer draws
 * (apps/web/src/scene/town/residential.ts, billboards.ts + signs/signMeshes.ts, civic.ts,
 * civicShops.ts, streetBuilder.ts awnings, buildings.ts window boxes) so visuals do not move. The
 * renderer draws hedges, For Sale signs, mailboxes and billboards FROM this list, so what you see is
 * what blocks you; the server adds every `solid` piece to the street colliders (streetColliders).
 *
 * Rule: a piece 0.25 m tall or more that reaches below 2 m blocks walking. Elevated parts (a
 * billboard panel 2.75 m up) do not.
 */
export type StreetDecorKind =
  /** Owned plot: low hedge either side of the front path (hidden when a front fence is placed). */
  | "hedge"
  /** Owned plot: mailbox on a post beside the front path. */
  | "mailbox"
  /** Unowned plot: the "For Sale" board on two posts. */
  | "for_sale_sign"
  /** Unowned plot: low L-shaped hedge arm at a lot corner. */
  | "lot_hedge"
  /** Unowned plot: corner stake where the two hedge arms meet. */
  | "lot_stake"
  | "billboard_leg"
  /** The billboard structure above the legs (frame, catwalk, lamps). Elevated: not solid. */
  | "billboard_panel"
  /** Real Estate office: entrance columns either side of the door. */
  | "entrance_column"
  /** Real Estate office: corner pilasters standing proud of the front wall. */
  | "pilaster"
  /** Home Builders: timber corner posts. */
  | "corner_post"
  /** Home Builders: pergola posts in front of the barn doors. */
  | "pergola_post"
  /** Modern mid-rise shops: canopy posts on the sidewalk. */
  | "awning_post"
  /** Small shops: planter boxes under the ground-floor windows. */
  | "window_box"
  /** Attractions (world v4): a statue or sculpture base in a side yard (the Downs horse, the Sports Center ball). */
  | "plinth"
  /** Townsy Downs: white paddock rails across the front of a side yard. */
  | "venue_fence"
  /** Monster Truck Arena: the show ramp in a side yard. */
  | "dirt_ramp"
  /** Monster Truck Arena: a stack of giant tyres in a side yard. */
  | "tyre_stack";

export interface StreetDecorPiece {
  /** Stable id, e.g. "hedge:r-01:a", "bb:bb-1:leg:1". */
  id: string;
  kind: StreetDecorKind;
  /** The plot it belongs to (plot decor only). */
  plotId?: string;
  /** Ground-plane footprint (world metres). */
  aabb: Aabb;
  /** Top of the piece above the ground (metres). */
  height: number;
  /** Bottom of the piece above the ground (0 for things standing on the ground). */
  baseY: number;
  /** Facing of the piece (yaw, 0 = +z): the plot facing for hedges/mailboxes, the sign facing for signs. */
  yaw: number;
  /** True when it blocks walking (height >= 0.25 m and it reaches below 2 m). */
  solid: boolean;
}

/** Pieces whose bottom is at least this high do not block walking (you pass beneath). */
export const DECOR_CLEARANCE_HEIGHT = 2;

/**
 * Heights shared with the attraction exteriors (apps/web/src/scene/town/attractions.ts), metres: the
 * casino marquee's underside (its columns end there), the Downs arch's springing line (its piers'
 * top), the Sports Center canopy's underside, the truck arena portal's lintel, and its ramp / tyre
 * stacks.
 */
export const VENUE_EXTERIOR = {
  casinoMarqueeY: 4.4,
  downsArchSpringY: 4.0,
  sportsCanopyY: 3.9,
  truckPortalY: 4.9,
  truckRampHeight: 2.0,
  truckTyreStackHeight: 2.7,
} as const;

// ---- Residential (apps/web/src/scene/town/residential.ts) -------------------------------------
/** The plot front edge is this far in front of the door plane. */
const DOOR_SETBACK = 2;
/** Hedges: 0.75 m tall, 0.7 m deep, centred 0.55 m inside the plot front, from 1.6 m beside the path to 2.2 m short of the plot side. */
const HEDGE = { height: 0.75, depth: 0.7, inset: 0.55, pathGap: 1.6, endGap: 2.2 } as const;
/** Mailbox: post at (1.2, front - 1.1) in the door frame, 0.42 x 0.28 box on top (top 1.22 m). */
const MAILBOX = { x: 1.2, inset: 1.1, width: 0.42, depth: 0.28, height: 1.22 } as const;
/** For Sale board 2.4 x 1.6 centred 1.65 m up, two 0.1 m posts 0.11 m beside it, 0.12 m above it. */
const LOT_SIGN = { width: 2.4, height: 1.6, centreY: 1.65 } as const;
/** Lot corner hedges: 1.8 m arms, 0.4 m thick, 0.45 m tall, 0.45 m in from the plot corner; 0.16 m stake cap, top 1.01 m. */
const LOT_CORNER = { arm: 1.8, thick: 0.4, height: 0.45, inset: 0.45, stake: 0.16, stakeTop: 1.01 } as const;

// ---- Billboards (apps/web/src/scene/signs/signMeshes.ts billboardStructureParts) ---------------
const BILLBOARD_LEG = { diameter: 0.4, inset: 1.6, back: 0.3 } as const;

/** Babylon RotationY convention (LocalBuilder.toWorld): world = (ox + x cos + z sin, oz - x sin + z cos). */
function frameRect(ox: number, oz: number, yaw: number, x0: number, x1: number, z0: number, z1: number): Aabb {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const xs: number[] = [];
  const zs: number[] = [];
  for (const [x, z] of [
    [x0, z0],
    [x1, z0],
    [x1, z1],
    [x0, z1],
  ] as const) {
    xs.push(round(ox + x * c + z * s));
    zs.push(round(oz - x * s + z * c));
  }
  return { minX: Math.min(...xs), minZ: Math.min(...zs), maxX: Math.max(...xs), maxZ: Math.max(...zs) };
}

/** Snap away floating noise from cos/sin of quarter turns (1e-9 m). */
function round(v: number): number {
  return Math.round(v * 1e9) / 1e9;
}

function box(cx: number, cz: number, w: number, d: number): Aabb {
  return { minX: cx - w / 2, minZ: cz - d / 2, maxX: cx + w / 2, maxZ: cz + d / 2 };
}

function piece(id: string, kind: StreetDecorKind, aabb: Aabb, height: number, yaw: number, plotId?: string, baseY = 0): StreetDecorPiece {
  const solid = height >= SOLID_MIN_HEIGHT && baseY < DECOR_CLEARANCE_HEIGHT;
  return plotId === undefined ? { id, kind, aabb, height, baseY, yaw, solid } : { id, kind, plotId, aabb, height, baseY, yaw, solid };
}

function ownedPlotDecor(plot: Plot, fenced: boolean): StreetDecorPiece[] {
  // Door frame: origin at the door, local +z out of the front toward the street (homes.ts homeFrame).
  const ox = plot.door.position.x;
  const oz = plot.door.position.z;
  const yaw = plot.facing;
  const out: StreetDecorPiece[] = [];
  if (!fenced) {
    const half = (plot.bounds.maxZ - plot.bounds.minZ) / 2;
    const len = half - HEDGE.endGap;
    const zc = DOOR_SETBACK - HEDGE.inset;
    for (const side of [-1, 1] as const) {
      const x0 = side * HEDGE.pathGap;
      const x1 = side * (HEDGE.pathGap + len);
      out.push(piece(`hedge:${plot.id}:${side < 0 ? "a" : "b"}`, "hedge", frameRect(ox, oz, yaw, Math.min(x0, x1), Math.max(x0, x1), zc - HEDGE.depth / 2, zc + HEDGE.depth / 2), HEDGE.height, yaw, plot.id));
    }
  }
  const mz = DOOR_SETBACK - MAILBOX.inset;
  out.push(piece(`mailbox:${plot.id}`, "mailbox", frameRect(ox, oz, yaw, MAILBOX.x - MAILBOX.width / 2, MAILBOX.x + MAILBOX.width / 2, mz - MAILBOX.depth / 2, mz + MAILBOX.depth / 2), MAILBOX.height, yaw, plot.id));
  return out;
}

function lotDecor(plot: Plot): StreetDecorPiece[] {
  const out: StreetDecorPiece[] = [];
  const b = plot.bounds;
  const { arm, thick, height, inset, stake, stakeTop } = LOT_CORNER;
  for (const [x, sx, xn] of [
    [b.minX + inset, 1, "w"],
    [b.maxX - inset, -1, "e"],
  ] as const) {
    for (const [z, sz, zn] of [
      [b.minZ + inset, 1, "s"],
      [b.maxZ - inset, -1, "n"],
    ] as const) {
      const corner = `${zn}${xn}`;
      out.push(piece(`lot:${plot.id}:${corner}:x`, "lot_hedge", box(x + (sx * arm) / 2, z, arm, thick), height, 0, plot.id));
      out.push(piece(`lot:${plot.id}:${corner}:z`, "lot_hedge", box(x, z + (sz * arm) / 2, thick, arm), height, 0, plot.id));
      out.push(piece(`lot:${plot.id}:${corner}:stake`, "lot_stake", box(x, z, stake, stake), stakeTop, 0, plot.id));
    }
  }
  const ls = plot.lotSign;
  if (ls) {
    // signFrameNode: rotation y = facing + PI; the board and posts sit 0.045 m behind the front face.
    const half = LOT_SIGN.width / 2 + 0.11 + 0.05;
    const top = LOT_SIGN.centreY + LOT_SIGN.height / 2 + 0.12;
    out.push(piece(`sign:${plot.id}`, "for_sale_sign", frameRect(ls.position.x, ls.position.z, ls.facing + Math.PI, -half, half, -0.01, 0.1), top, ls.facing, plot.id));
  }
  return out;
}

function billboardDecor(bb: Billboard): StreetDecorPiece[] {
  const out: StreetDecorPiece[] = [];
  const n = { x: Math.sin(bb.facing), z: Math.cos(bb.facing) };
  const r = { x: Math.cos(bb.facing), z: -Math.sin(bb.facing) };
  const legH = bb.position.y - bb.height / 2 + 0.4;
  const off = bb.width / 2 - BILLBOARD_LEG.inset;
  for (const side of [-1, 1] as const) {
    const px = bb.position.x + r.x * side * off - n.x * BILLBOARD_LEG.back;
    const pz = bb.position.z + r.z * side * off - n.z * BILLBOARD_LEG.back;
    out.push(piece(`bb:${bb.id}:leg:${side}`, "billboard_leg", box(round(px), round(pz), BILLBOARD_LEG.diameter, BILLBOARD_LEG.diameter), legH, bb.facing));
  }
  // Frame (w + 0.5 wide, 0.04..0.50), the back face at +0.54 (two-sided boards, ADR-0023), catwalk (0.8 deep at
  // -0.3) and lamps (to -0.77) in the sign frame.
  const bottom = bb.position.y - (bb.height + 0.5) / 2;
  const top = bb.position.y + (bb.height + 0.5) / 2;
  const aabb = frameRect(bb.position.x, bb.position.z, bb.facing + Math.PI, -(bb.width / 2 + 0.25), bb.width / 2 + 0.25, -0.77, 0.54);
  out.push(piece(`bb:${bb.id}:panel`, "billboard_panel", aabb, top, bb.facing, undefined, bottom));
  return out;
}

/**
 * Ground pieces of the Townsy-run buildings: the Real Estate office's columns and pilasters, Home
 * Builders' posts, and (world v4, ADR-0015) the attractions' entrance columns / portal posts and the
 * statues, rails, ramp and tyres in their side yards. The renderer draws the attraction pieces FROM
 * this list (apps/web/src/scene/town/attractions.ts) and the older civic ones at the same numbers.
 */
export function civicStreetDecor(plot: Plot): StreetDecorPiece[] {
  return plot.type === "CIVIC" ? civicDecor(plot) : [];
}

function civicDecor(plot: Plot): StreetDecorPiece[] {
  const out: StreetDecorPiece[] = [];
  const bb = plot.buildBounds;
  const cx = (bb.minX + bb.maxX) / 2;
  const w = bb.maxX - bb.minX;
  const d = bb.maxZ - bb.minZ;
  const H = CIVIC_OFFICE.heightMetres;
  // Front frame at the front-wall centre, local +z out of the front (civicShops.ts frontFrame). The
  // attractions face south: their front wall is buildBounds.minZ.
  const ox = cx;
  const oz = plot.facing === 0 ? bb.maxZ : bb.minZ;
  const yaw = plot.facing;
  const at = (id: string, kind: StreetDecorKind, x: number, z: number, sw: number, sd: number, height: number, baseY = 0) =>
    out.push(piece(`civic:${plot.id}:${id}`, kind, frameRect(ox, oz, yaw, x - sw / 2, x + sw / 2, z - sd / 2, z + sd / 2), height, yaw, plot.id, baseY));
  switch (plot.civic?.kind) {
    case "real_estate_office":
      // civic.ts: columns (0.42 m bases) 0.22 m out at +-1.35 / +-1.95, entablature top 2.38 m;
      // corner pilasters 0.7 x 0.3 at 0.12 m out from 0.7 m up.
      for (const sx of [-1, 1]) for (const k of [1.35, 1.95]) at(`col:${sx * k}`, "entrance_column", sx * k, 0.22, 0.42, 0.42, 2.38);
      for (const sx of [-1, 1]) at(`pilaster:${sx}`, "pilaster", sx * (w / 2 - 0.35), 0.12, 0.7, 0.3, H - 0.3 + 0.7, 0.7);
      break;
    case "home_builders":
      // civicShops.ts buildHomeBuilders: 0.45 m timber corner posts, 0.22 m pergola posts 1.3 m out.
      for (const sx of [-1, 1]) for (const [z, n] of [
        [0.05, "f"],
        [-d - 0.05, "b"],
      ] as const) at(`corner:${n}${sx}`, "corner_post", sx * (w / 2 - 0.1), z, 0.45, 0.45, H);
      for (const sx of [-1, 1]) at(`pergola:${sx}`, "pergola_post", sx * 1.9, 1.3, 0.22, 0.22, 2.35);
      break;
    // World v4 attractions (48 x 36 m, front wall on the plot's front line + 0.5 m): entrance pieces stand in the
    // 0.5 m between the wall and the sidewalk, clear of the 4.8 m door; side-yard pieces stand in the 6 m strips
    // beside the building (local |x| 24..30), never in front of the plot.
    case "casino":
      // Gold columns under the marquee, two either side of the doors.
      for (const sx of [-1, 1]) for (const k of [3.4, 7.6]) at(`col:${sx * k}`, "entrance_column", sx * k, 0.27, 0.5, 0.44, VENUE_EXTERIOR.casinoMarqueeY);
      break;
    case "race_track":
      // The entrance arch's two piers; a horse statue on a plinth in the east yard; white paddock rails in front of both yards.
      for (const sx of [-1, 1]) at(`pier:${sx}`, "entrance_column", sx * 3.3, 0.25, 0.8, 0.4, VENUE_EXTERIOR.downsArchSpringY);
      at("plinth", "plinth", -27, -5, 2.4, 4.2, 1.0);
      for (const sx of [-1, 1]) at(`rail:${sx}`, "venue_fence", sx * 27, -0.6, 5.2, 0.16, 1.15);
      break;
    case "sports_center":
      // Steel posts of the glass entrance canopy; the giant ball on its plinth in the west yard.
      for (const sx of [-1, 1]) at(`post:${sx}`, "entrance_column", sx * 4.2, 0.27, 0.3, 0.3, VENUE_EXTERIOR.sportsCanopyY);
      at("plinth", "plinth", 27, -4.5, 2.6, 2.6, 1.0);
      break;
    case "truck_arena":
      // Hazard-striped portal posts; the show ramp in the west yard, two tyre stacks in the east yard.
      for (const sx of [-1, 1]) at(`portal:${sx}`, "entrance_column", sx * 3.4, 0.27, 0.6, 0.44, VENUE_EXTERIOR.truckPortalY);
      at("ramp", "dirt_ramp", 27, -6.5, 4, 9, VENUE_EXTERIOR.truckRampHeight);
      for (const z of [-3.5, -8.5]) at(`tyres:${z}`, "tyre_stack", -27, z, 2.4, 2.4, VENUE_EXTERIOR.truckTyreStackHeight);
      break;
    default:
      // Furnishings: everything at ground level stays inside the building footprint (recessed shopfront).
      break;
  }
  return out;
}

function commercialDecor(plot: Plot): StreetDecorPiece[] {
  const out: StreetDecorPiece[] = [];
  const bb = plot.buildBounds;
  const cx = (bb.minX + bb.maxX) / 2;
  // North-row shops face south (front wall = minZ); v3 south-side shops face north (front wall = maxZ).
  // Frame: origin at the front-wall centre turned facing + PI, so local -z points out of the front.
  const front = isFacingNorth(plot.facing) ? bb.maxZ : bb.minZ;
  const yaw = plot.facing + Math.PI;
  if (plot.commercialTemplate === "modern_midrise") {
    // streetBuilder.ts awningParts: frame at (cx, front) turned facing + PI, posts at (+-2.25, -1.65), 0.1 m, 2.08 m tall.
    for (const sx of [-1, 1]) {
      out.push(piece(`awning:${plot.id}:${sx}`, "awning_post", frameRect(cx, front, yaw, sx * 2.25 - 0.05, sx * 2.25 + 0.05, -1.7, -1.6), 2.08, plot.facing, plot.id));
    }
  }
  if (plot.commercialTemplate === "small_shop") {
    // buildings.ts smallShop: 2.2 x 0.4 planter boxes 0.02-0.42 m out from the wall, 0.27-0.77 m up, at cx +- 2.9.
    for (const sx of [-1, 1]) {
      const x = sx * 2.9;
      out.push(piece(`windowbox:${plot.id}:${sx}`, "window_box", frameRect(cx, front, yaw, x - 1.1, x + 1.1, -0.42, -0.02), 0.77, plot.facing, plot.id, 0.27));
    }
  }
  return out;
}

/** True for a yaw facing north (+z), i.e. a v3 south-side shop. */
function isFacingNorth(yaw: number): boolean {
  return Math.cos(yaw) > 0.5;
}

/**
 * Every street decor piece for an ownership state. Owned residential plots get hedges (unless a front
 * fence is placed: `fencedPlotIds`) and a mailbox; unowned ones get the For Sale sign and the lot
 * corner hedges. The server and the client MUST pass the same sets (from PropertyPublic) or
 * prediction drifts. Deterministic order: plots in manifest order, then billboards.
 * World v6 (ADR-0042): the decor of every plot and billboard of the manifest handed in: the whole
 * manifest means the whole town (townDecor, town.ts), a district slice that district (v5: the whole
 * manifest meant Central).
 */
export function streetDecor(
  manifest: WorldManifest,
  ownedResidentialIds: ReadonlySet<string> = new Set(),
  fencedPlotIdSet: ReadonlySet<string> = new Set(),
): StreetDecorPiece[] {
  const street = manifest;
  const out: StreetDecorPiece[] = [];
  for (const plot of street.plots) {
    if (plot.type === "RESIDENTIAL") {
      if (ownedResidentialIds.has(plot.id)) out.push(...ownedPlotDecor(plot, fencedPlotIdSet.has(plot.id)));
      else out.push(...lotDecor(plot));
    } else if (plot.type === "CIVIC") out.push(...civicDecor(plot));
    else out.push(...commercialDecor(plot));
  }
  for (const bb of street.billboards) out.push(...billboardDecor(bb));
  return out;
}

/** Collision boxes of the solid pieces (what streetColliders adds). */
export function streetDecorColliders(pieces: readonly StreetDecorPiece[]): Aabb[] {
  return pieces.filter((p) => p.solid).map((p) => p.aabb);
}
